import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { environment as ENV } from '../../environments/environment';
import { Utils } from '../core/Utils';
import {
    IEmployee,
    IEmployeeApplicationResult,
    IEmployeeApplicationCustomForm, IEmployeeApplicationEditModel, IEmployeeStructure, IEmployeeApplication
} from '../models/EmployeeApplication';
import { IPagingParams } from '../models/Common';
import { map } from 'rxjs/operators';

/**
 * Employee application service
 */
@Injectable({ providedIn: "root" })
export class EmployeeApplicationService {
    constructor(private readonly http: HttpClient) { }

    readonly apiUrl = `${ENV.apiUrl}/employeeApplications`;

    /**
     * Get employee data.
     * @param upn Target employee UPN. If not specified or insufficient permissions, current user name will be used.
     */
    getEmployee(upn?: string): Observable<IEmployee[]> {
        const url = `${this.apiUrl}/employee`;
        return this.http.get<IEmployee[]>(url, {
            params: Utils.sanitizeQueryParams({ upn })
        });
    }

    /**
     * Create an application.
     * @param application
     */
    create(application: IEmployeeApplicationEditModel): Observable<IEmployeeApplicationResult> {
        const form = new FormData();

        (application.Files || []).forEach(t => {
            if (t.data) {
                form.append('file[]', t.data, t.name || t.data.name);
            }
        });

        const app = {
            ...application
        };

        delete app.Files;

        form.append('application', encodeURIComponent(JSON.stringify(app)));

        return this.http.post<IEmployeeApplicationResult>(this.apiUrl, form);
    }

    /**
     * Get current user applications.
     */
    get(paging?: IPagingParams, filter?: {
        structureId?: string[],
        from?: string,
        to?: string,
        search?: string
    }): Observable<{ items: IEmployeeApplication[], total: number }> {
        return this.http.get<{ items: IEmployeeApplication[], total: number }>(this.apiUrl, {
            params: Utils.sanitizeQueryParams({
                ...filter,
                ...paging
            })
        });
    }

    /**
     * Get custom configuration form data.
     * @param code
     * @param upn
     */
    getCustomFormByCode(code: string, upn?: string): Observable<IEmployeeApplicationCustomForm> {
        const url = `${this.apiUrl}/forms/latest/${code}`;
        return this.http.get<IEmployeeApplicationCustomForm>(url, {
            params: Utils.sanitizeQueryParams({ upn })
        });
    }

    /**
     * Get custom form choices.
     * @param code
     * @param dataSource
     * @param upn
     * @param filter
     */
    getCustomFormChoices(code: string, dataSource: string, upn?: string, filter?: string): Observable<any[]> {
        const url = `${this.apiUrl}/forms/latest/${code}/${dataSource}`;

        return this.http.get<any[]>(url, {
            params: Utils.sanitizeQueryParams({ upn, filter })
        });
    }

    /**
     * Get available application types.
     * @returns
     */
    getTypes(): Observable<string[]> {
        const url = `${this.apiUrl}/types`;
        return this.http.get<string[]>(url);
    }

    /**
     * Get available user structures.
     * @returns
     */
    getStructures(): Observable<IEmployeeStructure[]> {
        const url = `${this.apiUrl}/structures`;
        return this.http.get<IEmployeeStructure[]>(url);
    }

    /**
     * Download application document.
     * @param id
     * @returns
     */
    download(id: number): Observable<any> {
        const url = `${this.apiUrl}/${id}/file`;
        return this.http.get(url, {
            responseType: 'blob',
            observe: 'response'
        }).pipe(map(response => {
            Utils.downloadResponseFile(response);
            return of(undefined);
        }));
    }
}
